import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Button,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	InputAdornment,
	IconButton,
	Menu,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
} from "@mui/material";
import AppLayout from "../../components/AppLayout";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import { VendaService } from "../../service/venda_service";

function Vendas() {
	const navigate = useNavigate();
	const [vendas, setVendas] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedItem, setSelectedItem] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [itemToDelete, setItemToDelete] = useState(null);

	const unidadeDistribuidoraId = localStorage.getItem("selected_unidade_id");

	const loadVendas = useCallback(async () => {
		setLoading(true);
		try {
			const vendaService = new VendaService();
			const result = await vendaService.getAll(unidadeDistribuidoraId, searchTerm);
			setVendas(result || []);
		} catch (error) {
			console.error("Erro ao carregar vendas:", error);
			alert(error.message || "Erro ao carregar vendas");
		} finally {
			setLoading(false);
		}
	}, [unidadeDistribuidoraId, searchTerm]);

	useEffect(() => {
		if (unidadeDistribuidoraId) {
			loadVendas();
		}
	}, [unidadeDistribuidoraId, loadVendas]);

	// Debounced search effect
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (unidadeDistribuidoraId) {
				loadVendas();
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchTerm]);

	const handleNovaCadastro = () => {
		navigate("/vendas/cadastro");
	};

	const handleSearchChange = (event) => {
		setSearchTerm(event.target.value);
	};

	const handleOpenMenu = (event, item) => {
		setAnchorEl(event.currentTarget);
		setSelectedItem(item);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
		setSelectedItem(null);
	};

	const handleEdit = () => {
		if (selectedItem) {
			navigate(`/vendas/cadastro/${selectedItem.id}`);
		}
		handleCloseMenu();
	};

	const handleRomaneio = () => {
		if (selectedItem) {
			navigate(`/vendas/romaneio/${selectedItem.id}`);
		}
		handleCloseMenu();
	};

	const handleDeleteClick = () => {
		setItemToDelete(selectedItem);
		setOpenDialog(true);
		handleCloseMenu();
	};

	const handleConfirmDelete = async () => {
		if (itemToDelete) {
			try {
				const vendaService = new VendaService();
				await vendaService.delete(itemToDelete.id);
				setOpenDialog(false);
				setItemToDelete(null);
				loadVendas();
			} catch (error) {
				console.error("Erro ao excluir venda:", error);
				alert(error.message || "Erro ao excluir venda");
			}
		}
	};

	const handleCancelDelete = () => {
		setOpenDialog(false);
		setItemToDelete(null);
	};

	const formatDate = (dateString) => {
		if (!dateString) return "-";
		const date = new Date(dateString);
		return date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
	};

	return (
		<AppLayout currentPage="vendas">
			<Box sx={{ maxWidth: 1400, mx: "auto" }}>
					{/* Header com busca */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 3,
						}}
					>
						<Typography
							variant="h4"
							sx={{
								color: "#666",
								fontWeight: 600,
							}}
						>
							Vendas
						</Typography>
						<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
							<TextField
								size="small"
								placeholder="Buscar..."
								value={searchTerm}
								onChange={handleSearchChange}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
								}}
								sx={{
									width: "300px",
									"& .MuiOutlinedInput-root": {
										borderRadius: "8px",
									},
								}}
							/>
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={handleNovaCadastro}
								sx={{
									bgcolor: "#1a9e8f",
									"&:hover": {
										bgcolor: "#138a7d",
									},
									textTransform: "none",
									px: 3,
									py: 1,
									borderRadius: "8px",
								}}
							>
								Nova Venda
							</Button>
						</Box>
					</Box>

					{/* Tabela */}
					<Paper
						sx={{
							bgcolor: "white",
							borderRadius: "12px",
							boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
							overflow: "hidden",
						}}
					>
						{loading ? (
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									minHeight: "400px",
								}}
							>
								<CircularProgress sx={{ color: "#1a9e8f" }} />
							</Box>
						) : vendas.length === 0 ? (
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									minHeight: "400px",
									gap: 2,
									p: 4,
								}}
							>
								<Typography
									variant="h6"
									sx={{ color: "#999", textAlign: "center" }}
								>
									Nenhuma venda encontrada
								</Typography>
								<Typography
									variant="body2"
									sx={{ color: "#999", textAlign: "center" }}
								>
									{searchTerm
										? "Tente fazer uma busca diferente"
										: 'Clique no botão "Nova Venda" para começar'}
								</Typography>
							</Box>
						) : (
							<TableContainer>
								<Table>
									<TableHead>
										<TableRow sx={{ bgcolor: "#f5f5f5" }}>
											<TableCell sx={{ fontWeight: 600, color: "#666" }}>
												Data de Expedição
											</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#666" }}>
												Venda
											</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#666" }}>
												Cliente
											</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#666" }}>
												Itens
											</TableCell>
											<TableCell sx={{ fontWeight: 600, color: "#666" }}>
												Nota Fiscal
											</TableCell>
											<TableCell
												sx={{ fontWeight: 600, color: "#666", width: "80px" }}
												align="center"
											>
												Ações
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{vendas.map((venda) => (
											<TableRow
												key={venda.id}
												sx={{
													"&:hover": {
														bgcolor: "#f9f9f9",
													},
												}}
											>
												<TableCell>
													<Typography variant="body2">
														{formatDate(venda.dataExpedicao)}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography variant="body2" sx={{ fontWeight: 500 }}>
														{venda.numeroVenda}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{venda.cliente?.nome || "-"}
													</Typography>
													{venda.cliente?.nomeFantasia && (
														<Typography
															variant="caption"
															sx={{ color: "#999", display: "block" }}
														>
															{venda.cliente.nomeFantasia}
														</Typography>
													)}
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{venda.totalItens || 0}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{venda.notaFiscal || "-"}
													</Typography>
												</TableCell>
												<TableCell align="center">
													<IconButton
														size="small"
														onClick={(e) => handleOpenMenu(e, venda)}
														sx={{ color: "#666" }}
													>
														<MoreVertIcon />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Paper>
			</Box>

			{/* Menu de ações */}
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleCloseMenu}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
			>
				<MenuItem onClick={handleEdit}>
					<EditIcon fontSize="small" sx={{ mr: 1, color: "#1a9e8f" }} />
					Editar
				</MenuItem>
				<MenuItem onClick={handleRomaneio}>
					<DescriptionIcon fontSize="small" sx={{ mr: 1, color: "#1a9e8f" }} />
					Gerar romaneio de vendas
				</MenuItem>
				<MenuItem onClick={handleDeleteClick}>
					<DeleteIcon fontSize="small" sx={{ mr: 1, color: "#ff4d4d" }} />
					Excluir
				</MenuItem>
			</Menu>

			{/* Dialog de confirmação */}
			<Dialog open={openDialog} onClose={handleCancelDelete}>
				<DialogTitle>Confirmar Exclusão</DialogTitle>
				<DialogContent>
					<Typography>
						Tem certeza que deseja excluir a venda {itemToDelete?.numeroVenda}?
					</Typography>
					<Typography variant="caption" sx={{ color: "#999", mt: 1, display: "block" }}>
						Esta ação não poderá ser desfeita. O estoque será devolvido automaticamente.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelDelete} sx={{ color: "#666" }}>
						Cancelar
					</Button>
					<Button
						onClick={handleConfirmDelete}
						variant="contained"
						sx={{
							bgcolor: "#ff4d4d",
							"&:hover": {
								bgcolor: "#e64545",
							},
						}}
					>
						Confirmar
					</Button>
				</DialogActions>
			</Dialog>
		</AppLayout>
	);
}

export default Vendas;
